import React from "react";
import { useNavigate } from "react-router-dom";
import { AreaChart, Area, ResponsiveContainer, Tooltip } from "recharts";
import {
  UserPlus, Users2, Clock, DollarSign, TrendingUp, Scale,
  ShieldCheck, CalendarClock, ArrowRight, AlertCircle
} from "lucide-react";
import styles from "./hrms.module.scss";

const sparkData = [
  {v:4},{v:7},{v:5},{v:9},{v:6},{v:11},{v:8},{v:13},{v:10},{v:15},{v:12},{v:18}
];

const MODULES = [
  { id:"recruitment", label:"Recruitment", desc:"Manage job postings, applicants and offer letters", icon:UserPlus, color:"#00C8FF", bg:"rgba(0,200,255,0.12)", route:"/admin/hrms/recruitment", stats:[{l:"Open Jobs",v:12},{l:"Applicants",v:48}] },
  { id:"employees",   label:"Employee Management", desc:"Employee directory, departments and onboarding", icon:Users2, color:"#7C5CFF", bg:"rgba(124,92,255,0.12)", route:"/admin/hrms/employees", stats:[{l:"Total Staff",v:124},{l:"Departments",v:8}] },
  { id:"attendance",  label:"Attendance & Leave", desc:"Track attendance, leaves and holiday calendar", icon:Clock, color:"#00E5A3", bg:"rgba(0,229,163,0.12)", route:"/admin/hrms/attendance", stats:[{l:"Present Today",v:98},{l:"On Leave",v:7}] },
  { id:"payroll",     label:"Payroll", desc:"Salary processing, payslips and tax management", icon:DollarSign, color:"#FFB800", bg:"rgba(255,184,0,0.12)", route:"/admin/hrms/payroll", stats:[{l:"This Month",v:"₹4.2L"},{l:"Processed",v:"98%"}] },
  { id:"performance", label:"Performance", desc:"Goals, KPIs, appraisals and 360° reviews", icon:TrendingUp, color:"#00C8FF", bg:"rgba(0,200,255,0.12)", route:"/admin/hrms/performance", stats:[{l:"Active Goals",v:56},{l:"Reviews Due",v:14}] },
  { id:"compliance",  label:"Compliance & Legal", desc:"Policies, statutory reports and documents", icon:Scale, color:"#7C5CFF", bg:"rgba(124,92,255,0.12)", route:"/admin/hrms/compliance", stats:[{l:"Policies",v:18},{l:"Pending",v:3}] },
];

const ALERTS = [
  { type:"amber", msg:"7 employees' leave requests pending approval" },
  { type:"red",   msg:"Payroll processing due in 3 days" },
  { type:"cyan",  msg:"4 new applicants for Senior React Developer" },
  { type:"amber", msg:"3 compliance documents expiring this month" },
];

const HrmsDashboard = () => {
  const navigate = useNavigate();
  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>HRMS Platform</div>
          <h2>HR Command Centre</h2>
          <p>Complete overview of your workforce, payroll and compliance</p>
        </div>
      </div>

      {/* Top stats */}
      <div className={`${styles.statsGrid} ${styles.cols5}`} style={{ marginBottom:28 }}>
        {[
          { label:"Total Employees", val:124, color:"cyan",   trend:"↑ 5", dir:"up" },
          { label:"Open Positions",  val:12,  color:"purple", trend:"↑ 3", dir:"up" },
          { label:"Present Today",   val:98,  color:"green",  trend:"→ 0%",dir:"flat" },
          { label:"On Leave",        val:7,   color:"amber",  trend:"↓ 2", dir:"down" },
          { label:"Payroll Due",     val:3,   color:"red",    trend:"days", dir:"flat" },
        ].map(({ label, val, color, trend, dir }) => (
          <div key={label} className={`${styles.statCard} ${styles[color]}`}>
            <div className={`${styles.statTrend} ${styles[dir]}`}>{trend}</div>
            <div className={styles.statVal}>{val}</div>
            <div className={styles.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      {/* Alerts */}
      <div className={styles.card} style={{ marginBottom:24 }}>
        <div className={styles.cardHeader}><h3>⚡ Action Required</h3></div>
        <div className={styles.cardBody} style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {ALERTS.map((a, i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", background:"rgba(255,255,255,0.02)", borderRadius:8, border:`1px solid rgba(255,255,255,0.05)` }}>
              <AlertCircle size={14} style={{ color: a.type==="red"?"#FF4D6D":a.type==="amber"?"#FFB800":"#00C8FF", flexShrink:0 }} />
              <span style={{ fontSize:13, color:"#E8F0FE" }}>{a.msg}</span>
              <ArrowRight size={13} style={{ marginLeft:"auto", color:"#3D5473", cursor:"pointer" }} />
            </div>
          ))}
        </div>
      </div>

      {/* Module grid */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16 }}>
        {MODULES.map((m) => {
          const Icon = m.icon;
          return (
            <div key={m.id} className={styles.moduleCard}
              style={{ ["--accent"]: m.color }}
              onClick={() => navigate(m.route)}>
              <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:`linear-gradient(90deg,${m.color},transparent)`, borderRadius:"14px 14px 0 0" }} />
              <div className={styles.moduleIcon} style={{ background:m.bg, color:m.color }}>
                <Icon size={20} />
              </div>
              <h4>{m.label}</h4>
              <p>{m.desc}</p>
              <div className={styles.moduleStats}>
                {m.stats.map((s) => (
                  <div key={s.l} className={styles.ms}><span>{s.v}</span> {s.l}</div>
                ))}
              </div>
              <div style={{ position:"absolute", right:0, bottom:0, width:80, height:40, opacity:0.3 }}>
                <ResponsiveContainer><AreaChart data={sparkData}><Area type="monotone" dataKey="v" stroke={m.color} fill={m.color} fillOpacity={0.2} dot={false} /></AreaChart></ResponsiveContainer>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
export default HrmsDashboard;
